import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { UpdateProjectMutation, UpdateProjectWithVersionMutation } from "@/graphql/mutations/UpdateProjectWithVersionMutation";
import {
  UpdateProjectStatusDocument,
  UpdateProjectStatusWithVersionDocument,
  type UpdateProjectStatusMutation,
  type UpdateProjectStatusMutationVariables,
  type UpdateProjectStatusWithVersionMutation,
  type UpdateProjectStatusWithVersionMutationVariables,
} from "@/graphql";
import {
  isVersionEntityTypeUnsupported,
  isVersioningSchemaMissing,
} from "@/pages/ProjectVersionHistory/services/versioningSchema";

import { buildProjectScalarVersionInput, type ProjectScalarVersionMeta } from "./projectScalarVersionInput";
import { ProjectWriteUnavailableError } from "./projectWriteErrors";

/**
 * The project row's own scalars, written straight to Hasura.
 *
 * The third write rail, after location and calculation (RTLCA-5404). It is here ahead of its
 * permission on purpose: `project` has select permissions only, for every role, so
 * `update_project_by_pk` does not exist in the schema yet and every call below raises
 * `ProjectWriteUnavailableError`. The caller treats that as "not deployed here" and falls back to the
 * REST PUT, so today nothing changes; the day the permission lands, this becomes the writer and the
 * `updatebaseinfo` 500 on `/base`-created projects stops mattering.
 *
 * Hand-authored types rather than generated ones for the same reason — see the codegen note on the
 * documents themselves.
 */

/** Exactly the columns the settings page's Project section owns. Snake-cased names are Hasura's camel. */
export type ProjectScalarInput = {
  projectId: string;
  name: string;
  projectNumber: string | null;
  clientName: string | null;
  builderName: string | null;
  lcaResponsibleName: string | null;
  numberOfPeople: number | null;
  buildingTypeId: number;
  /** Nullable: `project_status_id` has no NOT NULL constraint and `/base` never sets it. */
  projectStatusId: number | null;
  yearOfConstruction: number;
  yearOfCommissioning: number;
};

export type ProjectScalarWriteResult = {
  /** Whether the bundled document carried the trail row, or the plain fallback ran. */
  versionLogged: boolean;
};

const projectSetInput = (input: ProjectScalarInput) => ({
  name: input.name,
  projectNumber: input.projectNumber,
  clientName: input.clientName,
  builderName: input.builderName,
  lcaResponsibleName: input.lcaResponsibleName,
  numberOfPeople: input.numberOfPeople,
  buildingTypeId: input.buildingTypeId,
  projectStatusId: input.projectStatusId,
  yearOfConstruction: input.yearOfConstruction,
  yearOfCommissioning: input.yearOfCommissioning,
});

/**
 * `entityType: "project"` is what the trail's check constraint already accepts, so no Hasura
 * migration is needed for the subject. What the row SAYS is `buildProjectScalarVersionInput`'s: the
 * fields that moved, their previous values and the labels behind the two ids (RTLCA-5820). It lives
 * in its own module so it can be tested without this one's Apollo client.
 */
export const updateProjectScalars = async (
  input: ProjectScalarInput,
  meta: ProjectScalarVersionMeta
): Promise<ProjectScalarWriteResult> => {
  const project = projectSetInput(input);

  const runPlain = async (): Promise<ProjectScalarWriteResult> => {
    const result = await gaiupApolloClient.mutate({
      mutation: UpdateProjectMutation,
      variables: { projectId: input.projectId, project },
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.project) throw new Error("The project row was not returned by Hasura.");
    return { versionLogged: false };
  };

  try {
    const result = await gaiupApolloClient.mutate({
      mutation: UpdateProjectWithVersionMutation,
      variables: { projectId: input.projectId, project, version: buildProjectScalarVersionInput(input, meta) },
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.project) throw new Error("The project row was not returned by Hasura.");
    return { versionLogged: true };
  } catch (mutationError) {
    if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) {
      throw mutationError;
    }

    // The same signature twice means it was never the trail that was missing: this environment
    // cannot write the project row at all, which is where every call lands until the permission
    // exists. `isVersioningSchemaMissing` matches "not found in type: 'mutation_root'", which is
    // exactly what a role without an update permission sees.
    try {
      return await runPlain();
    } catch (fallbackError) {
      if (isVersioningSchemaMissing(fallbackError)) throw new ProjectWriteUnavailableError(fallbackError);
      throw fallbackError;
    }
  }
};

/** One column of the project row: the status the dashboard's dialog changes (RTLCA-5645). Labels feed the trail row. */
export type ProjectStatusInput = {
  projectId: string;
  projectStatusId: number;
  statusLabel: string;
  previousStatusLabel: string | null;
};

const statusVersionInput = (input: ProjectStatusInput) => ({
  projectId: input.projectId,
  entityType: "project",
  operation: "update",
  summary: "Project status updated",
  entityLabel: input.statusLabel,
  changes: [{ field: "Project status", from: input.previousStatusLabel, to: input.statusLabel }],
});

export const updateProjectStatus = async (input: ProjectStatusInput): Promise<ProjectScalarWriteResult> => {
  const variables = { projectId: input.projectId, projectStatusId: input.projectStatusId };

  const runPlain = async (): Promise<ProjectScalarWriteResult> => {
    const result = await gaiupApolloClient.mutate<UpdateProjectStatusMutation, UpdateProjectStatusMutationVariables>({
      mutation: UpdateProjectStatusDocument,
      variables,
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.project) throw new Error("The project row was not returned by Hasura.");
    return { versionLogged: false };
  };

  try {
    const result = await gaiupApolloClient.mutate<
      UpdateProjectStatusWithVersionMutation,
      UpdateProjectStatusWithVersionMutationVariables
    >({
      mutation: UpdateProjectStatusWithVersionDocument,
      variables: { ...variables, version: statusVersionInput(input) },
      context: { tolerateMissingSchema: true },
    });
    if (!result.data?.project) throw new Error("The project row was not returned by Hasura.");
    return { versionLogged: true };
  } catch (mutationError) {
    if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) {
      throw mutationError;
    }
    try {
      return await runPlain();
    } catch (fallbackError) {
      if (isVersioningSchemaMissing(fallbackError)) throw new ProjectWriteUnavailableError(fallbackError);
      throw fallbackError;
    }
  }
};

export default updateProjectScalars;
